import React, { useState, useEffect } from 'react';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  SearchIcon, 
  FilterIcon,
  EyeIcon,
  RefreshCw 
} from 'lucide-react';
import api from '../../axios'; // Axios instance

const AdminProjectTable = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'updated_at',
    direction: 'desc'
  });

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    if(loggedInUser?.role_id == 0){
      window.location.href = "/home"; // Redirect if not logged in
      return;
    }
    if (!loggedInUser?.id) {
      window.location.href = "/login"; // Redirect if not logged in
      return;
    }
    
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/projects/recent`);
        console.log(response.data);
        if (Array.isArray(response.data.projects)) {
          setProjects(response.data.projects);
        } else {
          console.error("Invalid API response: expected an array");
          setProjects([]); // Fallback to empty array
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        setError("Failed to load projects");
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);

  // Validate projects to avoid errors with malformed data
  const validProjects = Array.isArray(projects) ? projects : [];

  // Calculate project stats
  const projectStats = {
    total: validProjects.length,
    approved: validProjects.filter((p) => p.status === "approved").length,
    pending: validProjects.filter((p) => p.status === "pending").length,
    rejected: validProjects.filter((p) => p.status === "rejected").length,
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted projects
  const sortedProjects = [...validProjects].sort((a, b) => {
    if (sortConfig.key === 'updated_at') {
      return sortConfig.direction === 'asc' 
        ? new Date(a.updated_at) - new Date(b.updated_at)
        : new Date(b.updated_at) - new Date(a.updated_at);
    }
    
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Filter projects based on search term
  const filteredProjects = sortedProjects.filter(project => 
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to determine status color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Function to render sort indicator
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon size={16} className="inline ml-1" />
    ) : (
      <ChevronDownIcon size={16} className="inline ml-1" />
    );
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header with stats */}
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <h2 className="text-lg font-medium text-gray-900 mb-3 sm:mb-0">Projects</h2>
        <div className="flex space-x-4 items-center text-sm">
          <div className="px-3 py-1 rounded-full bg-blue-50">{projectStats.total} Total</div>
          <div className="px-3 py-1 rounded-full bg-green-50 text-green-700">{projectStats.approved} Approved</div>
          <div className="px-3 py-1 rounded-full bg-yellow-50 text-yellow-700">{projectStats.pending} Pending</div>
          <div className="px-3 py-1 rounded-full bg-red-50 text-red-700">{projectStats.rejected} Rejected</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex">
        {["pending", "approved", "rejected"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 mr-6 text-sm md:text-base ${
              activeTab === tab ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Search and filters */}
      <div className="px-6 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 bg-gray-50">
        <div className="relative w-full sm:w-64 mb-3 sm:mb-0">
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute left-3 top-2.5 text-gray-400">
            <SearchIcon size={18} />
          </div>
        </div>
        <div className="flex items-center">
          <button 
            onClick={() => window.location.reload()}
            className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            <RefreshCw size={16} className="mr-1" /> Refresh
          </button>
          <button className="ml-2 flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300">
            <FilterIcon size={16} className="mr-1" /> Filter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort('name')}
              >
                Project Name {getSortIcon('name')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort('client')}
              >
                Client {getSortIcon('client')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort('role_name')}
              >
                Stage {getSortIcon('role_name')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => requestSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-6 py-10 text-center text-sm text-gray-500">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                  <div className="mt-2">Loading projects...</div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5" className="px-6 py-10 text-center text-sm text-red-500">
                  {error}
                </td>
              </tr>
            ) : filteredProjects.filter(project => project.status === activeTab).length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-10 text-center text-sm text-gray-500">
                  No {activeTab} projects found
                </td>
              </tr>
            ) : (
              filteredProjects
                .filter(project => project.status === activeTab)
                .map(project => (
                  <tr key={project.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{project.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.client || "Unknown"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.role_name || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                        {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <a
                        href={`/project-view/${project.id}`}
                        className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <EyeIcon size={16} className="mr-1" /> Review
                      </a>
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Footer with pagination placeholder */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center">
        <div>
          Showing {filteredProjects.filter(project => project.status === activeTab).length} of {validProjects.filter(project => project.status === activeTab).length} projects
        </div>
        <div className="flex space-x-1">
          <button className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50">Previous</button>
          <button className="px-3 py-1 border border-gray-300 rounded-md bg-white text-blue-600 font-medium">1</button>
          <button className="px-3 py-1 border border-gray-300 rounded-md bg-white disabled:opacity-50">Next</button>
        </div>
      </div>
    </div>
  );
};

export default AdminProjectTable;